import React, { useState, useCallback, useEffect, useMemo } from "react";
import { isBrowser, isMobile } from 'react-device-detect';
import MovieInfo from "./movieinfo/MovieInfo";
import Buttons from "./Buttons";
import Filters from "./filter/Filters";
import NowShowing from "./search/NowShowing";
import { describeActiveFilters } from "./filter/filterOptions";
import Recommend from "./recommend/Recommend";
import Report from "./report/Report";
import NoMovieFound from "./movienotfound/NoMovieFound";
import MovieTable from "./movietable/MovieTable";
import Tour, { tourSteps } from "./tour/Tour";
import { useFullscreen } from "../hooks/useFullscreen";
import { CAN_RATE } from "./Globals";

// Overlays opened from the toolbar, as opposed to the film details on the main screen
const PANELS = ['filters', 'report', 'recommend', 'movie-table'];

export default function Skin({
    movie,
    nav,
    player,
    filterControls,
    searchControls,
    setKeepSkinVisible,
    tourRequest = 0,
}) {

    const {
        title,
        released,
        runtime,
        language,
        director,
        writer,
        ratings,
        imdb_id,
        trailer,
        movieFound,
        movieNotFound
    } = movie || {};

    const { isFullscreen, exitFullscreen } = useFullscreen();

    const {
        getNextMovie,
        getSpecificMovie,
        updateCurrentMovieKinoRating,
    } = nav || {};

    const {
        toggleMute,
        mute,
        hideVideo,
    } = player || {};

    const {
        filters,
        updateRange,
        toggleCheckbox,
        resetFilters,
    } = filterControls || {};

    const {
        searchQuery,
        searchIntent,
        searchLoading,
        showFilms,
        clearAll,
    } = searchControls || {};

    // Single-overlay state: 'filters' | 'report' | 'recommend' | 'movie-info' | 'movie-not-found' | 'movie-table' | null
    // Toolbar buttons open their panel; pressing the one already open does nothing. Home closes it.
    const [activeOverlay, setActiveOverlay] = useState(null);
    const openOverlay = useCallback((overlayType) => setActiveOverlay(overlayType), []);
    const closeOverlay = useCallback(() => {
        setActiveOverlay((prev) => (PANELS.includes(prev) ? null : prev));
    }, []);

    const openSearch = useCallback(() => openOverlay('filters'), [openOverlay]);
    const openRecommend = useCallback(() => openOverlay('recommend'), [openOverlay]);
    const openMovieTable = useCallback(() => openOverlay('movie-table'), [openOverlay]);
    const openReport = useCallback(() => openOverlay('report'), [openOverlay]);
    const showMovieInfo = useCallback(() => setActiveOverlay('movie-info'), []);

    const panelOpen = PANELS.includes(activeOverlay);

    // Keep movie-info and movie-not-found in the same overlay system: when no panel is
    // open, the overlay reflects the movie state
    useEffect(() => {
        if (panelOpen) return;
        if (movieFound) {
            setActiveOverlay('movie-info');
        } else if (movieNotFound) {
            setActiveOverlay('movie-not-found');
        } else {
            setActiveOverlay(null);
        }
    }, [movieFound, movieNotFound, panelOpen]);

    // The guided tour, started from the landing page. Each step opens the panel it needs.
    const steps = useMemo(() => tourSteps(isBrowser), []);
    const [tourStep, setTourStep] = useState(null);
    useEffect(() => {
        if (tourRequest) setTourStep(0);
    }, [tourRequest]);
    useEffect(() => {
        if (tourStep === null) return;
        const wanted = steps[tourStep].overlay;
        setActiveOverlay((prev) => wanted || (PANELS.includes(prev) ? null : prev));
    }, [tourStep, steps]);
    const endTour = useCallback(() => {
        setTourStep(null);
        closeOverlay();
    }, [closeOverlay]);
    const tourActive = tourStep !== null;

    useEffect(() => {
        if (activeOverlay === 'movie-info') setKeepSkinVisible(false);
        if (panelOpen) setKeepSkinVisible(true);
    }, [activeOverlay, panelOpen, setKeepSkinVisible]);

    // A trailer starting to play resets keepSkinVisible (see Layout's onPlay), which would
    // fade an open panel or the tour. Re-assert it.
    useEffect(() => {
        if (panelOpen || tourActive) setKeepSkinVisible(true);
    });

    const renderMovieInfo = () => {
        return (
            <div className="row lower-skin" data-tour="film">
                <MovieInfo
                    title={title}
                    released={released}
                    runtime={runtime}
                    language={language}
                    director={director}
                    writer={writer}
                    ratings={ratings}
                />
            </div>
        )
    }

    // "Show films": run the description (if any) with every filter. The overlay stays open,
    // with a spinner on the button, until the first film is ready.
    const [showingFilms, setShowingFilms] = useState(false);
    const onShowFilms = async (query) => {
        if (showingFilms) return;
        setShowingFilms(true);
        if (typeof hideVideo === 'function') hideVideo();
        try {
            await showFilms(query);
        } finally {
            setShowingFilms(false);
            closeOverlay();
        }
    }

    const renderFilters = () => {
        return (
            <Filters
                filters={filters}
                toggleCheckbox={toggleCheckbox}
                updateRange={updateRange}
                searchQuery={searchQuery}
                onShowFilms={onShowFilms}
                onReset={resetFilters}
                busy={showingFilms}
            />
        )
    }

    // Desktop only: on phones the line clashes with the exit-fullscreen X
    const statusParts = describeActiveFilters(filters, searchQuery, searchIntent);
    const showStatus = !isMobile && activeOverlay !== 'filters' && (statusParts.length > 0 || searchLoading);

    const renderRecommendWindow = () => {
        return (
            <Recommend
                toggleRecommend={closeOverlay}
                imdb_id={imdb_id}
                kino_rating={ratings?.kino_rating}
                onRatingSaved={updateCurrentMovieKinoRating}
            />
        )
    }

    const stopAll = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const renderExitFullscreen = () => {
        return (
            <button
                type="button"
                className="mobile-exit"
                aria-label="Exit fullscreen"
                onClick={(e) => { stopAll(e); exitFullscreen(); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
                    <path d="M5 5l14 14M19 5L5 19" />
                </svg>
            </button>
        )
    }

    return (
        <div className={`container-flex pt-4 pr-7 pl-7 skin${activeOverlay === 'filters' ? ' is-filtering' : ''}${showStatus ? ' has-status' : ''}`}>
            {isMobile && isFullscreen ? renderExitFullscreen() : null}
            <Buttons
                getNextMovie={getNextMovie}
                showMovieInfo={showMovieInfo}
                hideVideo={hideVideo}
                toggleMute={toggleMute}
                mute={mute}
                openSearch={openSearch}
                openMovieTable={isBrowser ? openMovieTable : undefined}
                openReport={openReport}
                openRecommend={openRecommend}
                goHome={closeOverlay}
                searchVisible={activeOverlay === 'filters'}
                tableVisible={activeOverlay === 'movie-table'}
                reportingVisible={activeOverlay === 'report'}
                recommendVisible={activeOverlay === 'recommend'}
                homeActive={!panelOpen}
                showTableButton={isBrowser}
                showRateButton={CAN_RATE}
            />

            {showStatus ? <NowShowing parts={statusParts} loading={searchLoading} onClear={clearAll} /> : null}
            {CAN_RATE && activeOverlay === 'recommend' ? renderRecommendWindow() : null}
            {activeOverlay === 'filters' ? renderFilters() : null}
            {activeOverlay === 'report' ? (
                <Report toggleReporting={closeOverlay} getNextMovie={getNextMovie} imdb_id={imdb_id} trailer={trailer} />
            ) : null}

            {activeOverlay === 'movie-info' ? renderMovieInfo() : null}
            {activeOverlay === 'movie-not-found' ? <NoMovieFound /> : null}
            {activeOverlay === 'movie-table' ? (
                <MovieTable onClose={closeOverlay} getSpecificMovie={getSpecificMovie} filters={filters} searchQuery={searchQuery} />
            ) : null}

            {tourActive ? (
                <Tour
                    steps={steps}
                    step={tourStep}
                    onNext={() => setTourStep((s) => Math.min(s + 1, steps.length - 1))}
                    onBack={() => setTourStep((s) => Math.max(s - 1, 0))}
                    onEnd={endTour}
                />
            ) : null}
        </div>
    );
}
